import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { ClientSelectionService } from '../components/clients/client-selection.service';
import {
  AdjustersResponse,
  ClaimDetails,
  ClaimsResponse,
  ExcelUploadResponse,
  TransferClaimsRequest,
} from '../models/claim-reassignment.model';

@Injectable({
  providedIn: 'root',
})
export class ClaimReassignmentService {
  private readonly baseUrl = `${environment.apiUrl}claim-reassignment`;

  constructor(
    private http: HttpClient,
    private clientSelectionService: ClientSelectionService
  ) {}

  getAllAdjusters(): Observable<AdjustersResponse> {
    return this.http
      .get<any>(`${this.baseUrl}/getAllAdjusters`, { 
        headers: this.getHeaders(),
      })
      .pipe(
        map(res => {
          const raw = res.Adjusters ?? res.adjusters ?? [];
          const adjusters = raw.map((adj: any) => {
            const adjusterText =
              adj.AdjusterText ?? `${adj.AdjusterKey}-${adj.LastName},${adj.FirstName}`;
            const parsed = this.parseAdjusterText(adjusterText);

            return {
              adjusterID: Number(adj.AdjusterID ?? adj.adjusterID),
              adjusterText,
              adjusterKey: Number(
                adj.AdjusterKey ?? adj.adjusterKey ?? parsed.adjusterKey ?? 0
              ),
              firstName: (adj.FirstName ?? adj.firstName ?? parsed.firstName ?? '').toString(),
              lastName: (adj.LastName ?? adj.lastName ?? parsed.lastName ?? '').toString(),
            };
          });

          adjusters.unshift({ adjusterID: -1, adjusterText: 'Select an Adjuster' });

          return {
            adjusters,
            totalCount: res.TotalCount ?? res.totalCount ?? raw.length,
          };
        })
      );
  }

  getClaimsByAdjusterID(
    adjusterID: number,
    skip = 0,
    count = 40,
    sortBy?: string,
    sortDir?: 'asc' | 'desc'
  ): Observable<ClaimsResponse> {
    const params = new HttpParams()
      .set('adjusterID', adjusterID.toString())
      .set('skip', skip.toString())
      .set('count', count.toString())
      .set('sortBy', sortBy ?? '')
      .set('sortDir', sortDir ?? '');

    return this.http
      .get<any>(`${this.baseUrl}/claimsByAdjusterID`, {
        headers: this.getHeaders(),
        params,
      })
      .pipe(
        map(res => ({
          claims: (res.claims ?? []).map((c: any) => this.mapClaimDetails(c)),
          totalCount: res.totalCount ?? 0,
          activeCount: res.activeCount ?? 0,       
          deactivatedCount: res.deactivatedCount ?? 0 
        }))
      );
  }
  
  uploadExcel(
    file: File,
    fromAdjuster: number,
    toAdjuster: number,
    lastUpdatedBy?: string
  ): Observable<ExcelUploadResponse & { activeCount: number; deactivatedCount: number }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('fromAdjuster', fromAdjuster.toString());
    formData.append('toAdjuster', toAdjuster.toString());
    formData.append('lastUpdatedBy', String(lastUpdatedBy ?? '').trim());

    return this.http
      .post<any>(`${this.baseUrl}/upload-excel`, formData, { headers: this.getHeaders() })
      .pipe(
        map(res => {
          const claimsArray: ClaimDetails[] = (res.Claims ?? []).map((c: any) => this.mapClaimDetails(c));
          const { activeCount, deactivatedCount } = this.countClaimStatuses(claimsArray);

          return {
            claims: claimsArray,
            totalCount: res.TotalCount ?? claimsArray.length,
            adjusterTransferID: res.AdjusterTransferID,
            activeCount,
            deactivatedCount
          };
        })
      );
  }

  transferAllClaims(request: TransferClaimsRequest, lastUpdatedBy?: string): Observable<string> {
    const payload = {
      ...request,
      LastUpdatedBy: String(lastUpdatedBy ?? '').trim(),
    };

    return this.http.post(`${this.baseUrl}/transferAllClaims`, payload, {
      headers: this.getHeaders(),
      responseType: 'text',
    });
  }

  transferClaimsOnExcel(request: TransferClaimsRequest, lastUpdatedBy?: string): Observable<string> {
    const payload = {
      ...request,
      LastUpdatedBy: String(lastUpdatedBy ?? '').trim(),
    };

    return this.http.post(`${this.baseUrl}/transferClaimsOnExcel`, payload, {
      headers: this.getHeaders(),
      responseType: 'text',
    });
  }

  private getHeaders(): HttpHeaders {
    const clientDbName = this.clientSelectionService.getSelectedClientDbName();
    return new HttpHeaders({
      ClientDBName: clientDbName,
    });
  }

  private parseAdjusterText(adjusterText: string): {
    adjusterKey?: number;
    firstName?: string;
    lastName?: string;
  } {
    const [keySegment, nameSegment = ''] = adjusterText.split('-', 2);
    const key = Number((keySegment ?? '').trim());
    const [lastNameRaw = '', firstNameRaw = ''] = nameSegment.split(',', 2);

    return {
      adjusterKey: Number.isFinite(key) ? key : undefined,
      firstName: firstNameRaw.trim() || undefined,
      lastName: lastNameRaw.trim() || undefined,
    };
  }

  private mapClaimDetails(claim: any): ClaimDetails {
    return {
      claimKey: claim.ClaimKey ?? '',
      claimantKey: claim.ClaimantKey ?? '',
      insuranceType: claim.InsuranceType ?? '',
      claimNumber: claim.ClaimNumber ?? '',
      injuredPartyName: claim.InjuredPartyName ?? '',
      isDeactivated: claim.IsDeactivated ?? 'No',
    };
  }

  private countClaimStatuses(claims: ClaimDetails[]): {
    activeCount: number;
    deactivatedCount: number;
  } {
    return {
      activeCount: claims.filter(c => c.isDeactivated === 'No').length,
      deactivatedCount: claims.filter(c => c.isDeactivated === 'Yes').length,
    };
  }
}
